import React, { useState, useEffect } from 'react';
import { useConfig } from '../../context/ConfigContext';
import { useToast } from '../Toast';
import { C, S } from '../../utils/styles';

function TextField({ label, desc, value, onChange, multiline, rows }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ ...S.label, marginBottom: 2 }}>{label}</label>
      {desc && <div style={{ fontSize: 10, color: C.textLight, marginBottom: 4 }}>{desc}</div>}
      {multiline ? (
        <textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          rows={rows || 4}
          style={{ ...S.input, fontFamily: "'Inter', sans-serif", fontWeight: 400, fontSize: 12, height: 'auto', resize: 'vertical', lineHeight: 1.6 }}
        />
      ) : (
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          style={{ ...S.input, fontFamily: "'Inter', sans-serif", fontWeight: 400, fontSize: 12 }}
        />
      )}
    </div>
  );
}

function ArrayField({ label, desc, items, onChange, titleLabel, descLabel }) {
  const add = () => onChange([...items, { title: '', description: '' }]);
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const update = (idx, field, val) => {
    const updated = [...items];
    updated[idx] = { ...updated[idx], [field]: val };
    onChange(updated);
  };
  const moveUp = (idx) => {
    if (idx === 0) return;
    const updated = [...items];
    [updated[idx - 1], updated[idx]] = [updated[idx], updated[idx - 1]];
    onChange(updated);
  };
  const moveDown = (idx) => {
    if (idx >= items.length - 1) return;
    const updated = [...items];
    [updated[idx], updated[idx + 1]] = [updated[idx + 1], updated[idx]];
    onChange(updated);
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <div>
          <label style={{ ...S.label, marginBottom: 0 }}>{label}</label>
          {desc && <div style={{ fontSize: 10, color: C.textLight }}>{desc}</div>}
        </div>
        <button onClick={add} style={{ padding: '3px 10px', background: C.accentLight, borderRadius: 5, border: `1px solid ${C.accent}`, color: C.accent, fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>+ Add</button>
      </div>
      {items.map((item, idx) => (
        <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 70px', gap: 6, marginBottom: 4, alignItems: 'start' }}>
          <input
            type="text"
            value={item.title || ''}
            onChange={(e) => update(idx, 'title', e.target.value)}
            placeholder={titleLabel || 'Title'}
            style={{ ...S.input, fontSize: 11, padding: '5px 8px', fontFamily: "'Inter', sans-serif", fontWeight: 500 }}
          />
          <input
            type="text"
            value={item.description || ''}
            onChange={(e) => update(idx, 'description', e.target.value)}
            placeholder={descLabel || 'Description'}
            style={{ ...S.input, fontSize: 11, padding: '5px 8px', fontFamily: "'Inter', sans-serif", fontWeight: 400 }}
          />
          <div style={{ display: 'flex', gap: 2 }}>
            <button onClick={() => moveUp(idx)} disabled={idx === 0} style={{ padding: '3px 5px', background: C.bg, borderRadius: 3, border: `1px solid ${C.border}`, color: C.textLight, fontSize: 10, cursor: 'pointer', opacity: idx === 0 ? 0.3 : 1 }}>&#9650;</button>
            <button onClick={() => moveDown(idx)} disabled={idx >= items.length - 1} style={{ padding: '3px 5px', background: C.bg, borderRadius: 3, border: `1px solid ${C.border}`, color: C.textLight, fontSize: 10, cursor: 'pointer', opacity: idx >= items.length - 1 ? 0.3 : 1 }}>&#9660;</button>
            <button onClick={() => remove(idx)} style={{ padding: '3px 5px', background: C.redLight, borderRadius: 3, border: 'none', color: C.red, fontSize: 10, cursor: 'pointer' }}>&#10007;</button>
          </div>
        </div>
      ))}
    </div>
  );
}

const SECTIONS = [
  { id: 'cover', label: 'Cover Page' },
  { id: 'exec', label: 'Executive Summary' },
  { id: 'how', label: 'How It Works' },
  { id: 'employer', label: 'Employer Benefits' },
  { id: 'employee', label: 'Employee Benefits' },
  { id: 'plans', label: 'Plan Options' },
  { id: 'paycheck', label: 'Paycheck Example' },
  { id: 'financial', label: 'Financial Impact' },
  { id: 'roster', label: 'Employee Roster' },
  { id: 'next', label: 'Next Steps & Contact' },
  { id: 'global', label: 'Global Settings' },
];

export default function ReportContentEditor() {
  const { config, updateReportContent } = useConfig();
  const addToast = useToast();
  const [draft, setDraft] = useState(JSON.parse(JSON.stringify(config.reportContent || {})));
  const [hasChanges, setHasChanges] = useState(false);
  const [activeSection, setActiveSection] = useState('cover');

  useEffect(() => {
    console.log('📄 ReportContentEditor: config.reportContent =', config.reportContent);
    if (config.reportContent) {
      console.log('  Keys:', Object.keys(config.reportContent));
      console.log('  reportTitle:', config.reportContent.reportTitle);
    }
    setDraft(JSON.parse(JSON.stringify(config.reportContent || {})));
    setHasChanges(false);
  }, [config.reportContent]);

  const set = (key, val) => {
    setDraft(prev => ({ ...prev, [key]: val }));
    setHasChanges(true);
  };

  const save = () => {
    updateReportContent(draft);
    setHasChanges(false);
    addToast('Report content saved');
  };

  const cancel = () => {
    setDraft(JSON.parse(JSON.stringify(config.reportContent || {})));
    setHasChanges(false);
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'cover':
        return (
          <>
            <TextField label="Report Title" value={draft.report_title} onChange={v => set('report_title', v)} />
            <TextField label="Subtitle Prefix" desc='Appears before company name, e.g. "Prepared for"' value={draft.report_subtitle_prefix} onChange={v => set('report_subtitle_prefix', v)} />
            <TextField label="Confidentiality Notice" value={draft.confidentiality_notice} onChange={v => set('confidentiality_notice', v)} />
          </>
        );
      case 'exec':
        return (
          <>
            <TextField label="Headline" value={draft.exec_summary_headline} onChange={v => set('exec_summary_headline', v)} />
            <TextField label="Introduction" multiline rows={4} value={draft.exec_summary_intro} onChange={v => set('exec_summary_intro', v)} />
            <TextField label="Employer Callout" desc="Highlighted message about employer benefits" multiline rows={2} value={draft.exec_summary_employer_callout} onChange={v => set('exec_summary_employer_callout', v)} />
            <TextField label="Employee Callout" desc="Highlighted message about employee benefits" multiline rows={2} value={draft.exec_summary_employee_callout} onChange={v => set('exec_summary_employee_callout', v)} />
          </>
        );
      case 'how':
        return (
          <>
            <TextField label="Headline" value={draft.how_it_works_headline} onChange={v => set('how_it_works_headline', v)} />
            <TextField label="Body Text" desc="Use \n for line breaks" multiline rows={6} value={draft.how_it_works_body} onChange={v => set('how_it_works_body', v)} />
            <ArrayField label="Steps" items={draft.how_it_works_steps || []} onChange={v => set('how_it_works_steps', v)} titleLabel="Step label" descLabel="Step description" />
          </>
        );
      case 'employer':
        return (
          <>
            <TextField label="Headline" value={draft.employer_benefits_headline} onChange={v => set('employer_benefits_headline', v)} />
            <ArrayField label="Benefit Items" items={draft.employer_benefits_items || []} onChange={v => set('employer_benefits_items', v)} titleLabel="Benefit title" descLabel="Benefit description" />
          </>
        );
      case 'employee':
        return (
          <>
            <TextField label="Headline" value={draft.employee_benefits_headline} onChange={v => set('employee_benefits_headline', v)} />
            <TextField label="Introduction" multiline rows={3} value={draft.employee_benefits_intro} onChange={v => set('employee_benefits_intro', v)} />
            <ArrayField label="Benefit Categories" items={draft.employee_benefits_categories || []} onChange={v => set('employee_benefits_categories', v)} titleLabel="Category name" descLabel="Description" />
          </>
        );
      case 'plans':
        return (
          <>
            <TextField label="Headline" value={draft.plan_options_headline} onChange={v => set('plan_options_headline', v)} />
            <TextField label="Introduction" multiline rows={3} value={draft.plan_options_intro} onChange={v => set('plan_options_intro', v)} />
          </>
        );
      case 'paycheck':
        return (
          <>
            <TextField label="Headline" value={draft.paycheck_headline} onChange={v => set('paycheck_headline', v)} />
            <TextField label="Footnote" multiline rows={3} value={draft.paycheck_footnote} onChange={v => set('paycheck_footnote', v)} />
          </>
        );
      case 'financial':
        return (
          <>
            <TextField label="Headline" value={draft.financial_impact_headline} onChange={v => set('financial_impact_headline', v)} />
            <TextField label="Footnote" multiline rows={3} value={draft.financial_impact_footnote} onChange={v => set('financial_impact_footnote', v)} />
          </>
        );
      case 'roster':
        return (
          <TextField label="Headline" value={draft.roster_headline} onChange={v => set('roster_headline', v)} />
        );
      case 'next':
        return (
          <>
            <TextField label="Headline" value={draft.next_steps_headline} onChange={v => set('next_steps_headline', v)} />
            <ArrayField label="Steps" items={draft.next_steps_items || []} onChange={v => set('next_steps_items', v)} titleLabel="Step title" descLabel="Step description" />
            <TextField label="Timeline" value={draft.next_steps_timeline} onChange={v => set('next_steps_timeline', v)} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <TextField label="Contact Name" value={draft.contact_name} onChange={v => set('contact_name', v)} />
              <TextField label="Phone" value={draft.contact_phone} onChange={v => set('contact_phone', v)} />
              <TextField label="Email" value={draft.contact_email} onChange={v => set('contact_email', v)} />
              <TextField label="Website" value={draft.contact_website} onChange={v => set('contact_website', v)} />
            </div>
            <TextField label="Closing Message" multiline rows={2} value={draft.closing_message} onChange={v => set('closing_message', v)} />
          </>
        );
      case 'global':
        return (
          <>
            <TextField label="Report Disclaimer" desc="Appears on every page footer" multiline rows={4} value={draft.report_disclaimer} onChange={v => set('report_disclaimer', v)} />
            <div style={{ marginBottom: 14 }}>
              <label style={{ ...S.label, marginBottom: 4 }}>Show HC Revenue by Default</label>
              <div style={{ fontSize: 10, color: C.textLight, marginBottom: 6 }}>When enabled, HC spread revenue is shown in generated reports by default (can be toggled per-report)</div>
              <button
                onClick={() => set('show_hc_revenue_default', !draft.show_hc_revenue_default)}
                style={{
                  width: 40, height: 22, borderRadius: 11, border: 'none',
                  background: draft.show_hc_revenue_default ? C.green : '#ccc',
                  position: 'relative', cursor: 'pointer', transition: 'background 0.2s',
                }}
              >
                <div style={{
                  width: 16, height: 16, borderRadius: '50%', background: '#fff',
                  position: 'absolute', top: 3,
                  left: draft.show_hc_revenue_default ? 21 : 3,
                  transition: 'left 0.2s',
                }} />
              </button>
            </div>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div data-testid="report-content-editor" style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 14, alignItems: 'start' }}>
      {/* Section navigation */}
      <div style={{ ...S.card, padding: '12px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {SECTIONS.map(sec => (
            <button
              key={sec.id}
              data-testid={`report-section-${sec.id}`}
              onClick={() => setActiveSection(sec.id)}
              style={{
                padding: '7px 10px',
                borderRadius: 6,
                border: 'none',
                background: activeSection === sec.id ? C.accentBg : 'transparent',
                color: activeSection === sec.id ? C.accent : C.textMid,
                fontSize: 11,
                fontWeight: activeSection === sec.id ? 700 : 400,
                cursor: 'pointer',
                textAlign: 'left',
                fontFamily: "'Inter', sans-serif",
              }}
            >
              {sec.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content editor */}
      <div style={S.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h3 style={{ ...S.heading, margin: 0 }}>{SECTIONS.find(s => s.id === activeSection)?.label || ''}</h3>
            {hasChanges && <span style={{ fontSize: 9, color: C.orange, fontWeight: 700, background: C.orangeLight, padding: '2px 8px', borderRadius: 4 }}>Unsaved Changes</span>}
          </div>
        </div>
        {renderSection()}
        {hasChanges && (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
            <button 
              onClick={cancel} 
              style={S.buttonSecondary}
            >
              Cancel
            </button>
            <button 
              data-testid="save-report-content-btn" 
              onClick={save} 
              style={S.buttonPrimary}
            >
              Save Changes
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
